// Rule-based bot decision module (no Phaser, no LLM).
//
// Every decision passes through an accuracy wrapper: the best-ranked legal
// option is chosen with probability `accuracy`, otherwise a uniformly random
// legal option is chosen. Beginner accuracy = 25%; future bots (medium 50%,
// difficult 75%) only change the accuracy constant.

import type { Card } from './Cards';

export type BotState = {
    player: number;
    board: (number | null)[][];
    lockedCells: string[];
    hand: Card[];
    opponentHandCount: number;
    legalMoves: Move[];
    cardLockOwner: number | null;
    chainableCardId: string | null;
    canActivate: boolean;
    canDiscard: boolean;
};

export type Move = { from: [number, number]; to: [number, number] };
export type ActivationDecision = { card: Card; lockTarget?: { row: number; col: number } };

export const BEGINNER_ACCURACY = 0.25;
const MAX_HAND_SIZE = 3;

const WINNING_LINES: [number, number][][] = [
    [[0, 0], [0, 1], [0, 2]],
    [[1, 0], [1, 1], [1, 2]],
    [[2, 0], [2, 1], [2, 2]],
    [[0, 0], [1, 0], [2, 0]],
    [[0, 1], [1, 1], [2, 1]],
    [[0, 2], [1, 2], [2, 2]],
    [[0, 0], [1, 1], [2, 2]],
    [[0, 2], [1, 1], [2, 0]],
];

// Player 1 starts on the bottom row (2), Player 2 on the top row (0).
const STARTING_ROW: Record<number, number> = { 1: 2, 2: 0 };

// Accuracy wrapper: `best` with probability `accuracy`, else a uniform random
// member of `options` (which may include a null/skip option).
const choose = <T,>(best: T, options: T[], accuracy: number): T => {
    if (options.length === 0) {
        return best;
    }
    if (Math.random() < accuracy) {
        return best;
    }
    return options[Math.floor(Math.random() * options.length)];
};

const cloneBoard = (board: (number | null)[][]): (number | null)[][] => board.map((r) => [...r]);

const placePiece = (board: (number | null)[][], row: number, col: number, player: number): (number | null)[][] => {
    const next = cloneBoard(board);
    next[row][col] = player;
    return next;
};

const applyMove = (board: (number | null)[][], move: Move, player: number): (number | null)[][] => {
    const next = cloneBoard(board);
    next[move.from[0]][move.from[1]] = null;
    next[move.to[0]][move.to[1]] = player;
    return next;
};

const hasWon = (board: (number | null)[][], player: number): boolean => {
    const startRow = STARTING_ROW[player];
    return WINNING_LINES.some((line) => {
        // a line on the player's original starting row does not count
        if (line.every(([r]) => r === startRow)) {
            return false;
        }
        return line.every(([r, c]) => board[r][c] === player);
    });
};

// The empty cell that would complete a line for `player` (a winning-move
// target), or null. Approximates reachability for a one-ply beginner bot.
const winningCell = (board: (number | null)[][], player: number): [number, number] | null => {
    for (let r = 0; r < 3; r++) {
        for (let c = 0; c < 3; c++) {
            if (board[r][c] === null && hasWon(placePiece(board, r, c, player), player)) {
                return [r, c];
            }
        }
    }
    return null;
};

const opponent = (player: number): number => (player === 1 ? 2 : 1);

const isLocked = (lockedCells: string[], row: number, col: number): boolean =>
    lockedCells.includes(`${row},${col}`);

const emptyUnlockedCells = (state: BotState): [number, number][] => {
    const cells: [number, number][] = [];
    for (let r = 0; r < 3; r++) {
        for (let c = 0; c < 3; c++) {
            if (state.board[r][c] === null && !isLocked(state.lockedCells, r, c)) {
                cells.push([r, c]);
            }
        }
    }
    return cells;
};

const rankMove = (state: BotState, move: Move): number => {
    const next = applyMove(state.board, move, state.player);
    if (hasWon(next, state.player)) {
        return 4; // winning move
    }
    const oppWin = winningCell(state.board, opponent(state.player));
    if (oppWin && oppWin[0] === move.to[0] && oppWin[1] === move.to[1]) {
        return 3; // blocks the opponent's immediate win
    }
    if (winningCell(next, state.player) !== null) {
        return 2; // creates a two-in-a-line threat
    }
    return 1; // neutral
};

// Simple card value ranking: how much the bot wants to keep / play a card.
const cardValue = (id: Card['id']): number => {
    switch (id) {
        case 'draw-one':
            return 4;
        case 'throw-card':
            return 3;
        case 'card-lock':
            return 2;
        case 'lock-position':
            return 1;
    }
};

export const pickMove = (state: BotState, accuracy: number): Move | null => {
    if (state.legalMoves.length === 0) {
        return null;
    }
    const ranked = state.legalMoves.map((m) => ({ move: m, score: rankMove(state, m) }));
    const bestScore = Math.max(...ranked.map((r) => r.score));
    const bestMoves = ranked.filter((r) => r.score === bestScore).map((r) => r.move);
    const best = bestMoves[Math.floor(Math.random() * bestMoves.length)];
    return choose(best, state.legalMoves, accuracy);
};

export const pickLockTarget = (state: BotState, accuracy: number): { row: number; col: number } => {
    const cells = emptyUnlockedCells(state);
    const oppWin = winningCell(state.board, opponent(state.player));
    const hasCenter = cells.some(([r, c]) => r === 1 && c === 1);
    const best: { row: number; col: number } = oppWin
        ? { row: oppWin[0], col: oppWin[1] }
        : hasCenter
            ? { row: 1, col: 1 }
            : { row: cells[0][0], col: cells[0][1] };
    return choose(best, cells.map(([row, col]) => ({ row, col })), accuracy);
};

export const pickDiscard = (state: BotState, accuracy: number): Card | null => {
    if (!state.canDiscard || state.hand.length === 0) {
        return null;
    }
    // always free space when the hand is full; otherwise discard only on a roll
    if (state.hand.length < MAX_HAND_SIZE && Math.random() > accuracy) {
        return null;
    }
    const worst = state.hand.reduce((a, b) => (cardValue(a.id) <= cardValue(b.id) ? a : b));
    return choose(worst, state.hand, accuracy);
};

export const pickActivation = (state: BotState, accuracy: number): ActivationDecision | null => {
    if (!state.canActivate) {
        return null;
    }
    if (state.cardLockOwner !== null && state.cardLockOwner !== state.player) {
        return null; // cards are locked
    }
    const legal = (card: Card): boolean => {
        if (state.chainableCardId !== null) {
            return card.instanceId === state.chainableCardId;
        }
        switch (card.id) {
            case 'throw-card':
            case 'card-lock':
                return state.opponentHandCount > 0;
            case 'lock-position':
                return emptyUnlockedCells(state).length > 0;
            case 'draw-one':
                return true;
        }
    };
    const candidates = state.hand.filter(legal);
    if (candidates.length === 0) {
        return null;
    }
    // when only the chainable card is eligible, the bot decides whether to
    // chain it (probability = accuracy) or skip
    if (state.chainableCardId !== null) {
        const chainCard = candidates[0];
        return Math.random() < accuracy
            ? {
                card: chainCard,
                lockTarget: chainCard.id === 'lock-position' ? pickLockTarget(state, accuracy) : undefined,
            }
            : null;
    }
    const best = candidates.reduce((a, b) => (cardValue(a.id) >= cardValue(b.id) ? a : b));
    const chosen = choose<Card | null>(best, [...candidates, null], accuracy);
    if (!chosen) {
        return null;
    }
    return {
        card: chosen,
        lockTarget: chosen.id === 'lock-position' ? pickLockTarget(state, accuracy) : undefined,
    };
};

